import { node } from "~/util/dom";
import createSDSIcon from "~/ui/icon";
import { CLOSE_24 } from "@sznds/icons";
import { CONFIG, setGlobals, store, _ } from "./globals";
import { IStore } from "~/state/store";
import { watch, watchSubscriptions } from "~/state/util";
import { Unsubscribe } from "redux";
import { ISubscriptionWithDescription } from "~/util/providers/share-position/interfaces";
import { selectActiveSubscription, selectSubscriptions } from "~/state/share-position/selectors";
import SubscriptionItem from "~/ui/share-position/subscription-item";
import { IConfig } from "~/config";
import { toggleActiveSubscription, setSubscriptions } from "~/state/share-position/state";
import { deleteSubscription, subscriptionsMe } from "~/util/providers/share-position/subscriptions";
import { openOrInstallApplication } from "~/util/userweb";

export function initModule(global: {
	store: IStore;
	CONFIG: IConfig;
	Scene: any;
	_: (keyInput: string, ...rest: any[]) => string;
}) {
	setGlobals(global);
}

export default class SharePosition {
	#container: HTMLDivElement;

	#emptyInfo: HTMLParagraphElement;

	#list: HTMLUListElement;

	#promo: HTMLButtonElement;

	#storeUnsubscribeSharePosition: Unsubscribe;

	#storeUnsubscribeSharePositionActive: Unsubscribe;

	#subscriptions: { [key: string]: SubscriptionItem };

	constructor() {
		this.#subscriptions = {};
		this.#container = node("div", { className: "shareposition" });

		// hlavicka
		const header = node("div", { className: "shareposition__header" });
		const closeButton = node("button", { className: "shareposition__closeButton" });

		closeButton.appendChild(createSDSIcon({ symbol: CLOSE_24 }));
		closeButton.addEventListener("click", () => {
			Mapy.getComponent("sharePosition").deactivate();
			Mapy.getComponent("history").save();
		}, false);
		header.appendChild(node("h1", { className: "title", textContent: _("sharePosition.title") }));
		header.appendChild(closeButton);

		this.#container.appendChild(header);

		// prazdno - info
		this.#emptyInfo = node("p", { className: "shareposition__emptyInfo", innerText: _("sharePosition.emptyInfo") });

		// kontajner pro jednotlive polozky
		this.#list = node("ul", { className: "shareposition__list" });

		// promo banner
		const lang = ["cs", "sk"].indexOf(CONFIG.defaultLang) === -1 ? "en" : "cs";

		this.#promo = node("button", {
			className: "shareposition__promo",
			innerHTML: lang === "cs"
				? `<img src="/img/share-position/share-position.png" srcset="/img/share-position/share-position.png 1x, /img/share-position/share-position@2x.png 2x, /img/share-position/share-position@3x.png" />`
				: `<img src="/img/share-position/share-position-en.png" srcset="/img/share-position/share-position-en.png 1x, /img/share-position/share-position-en@2x.png 2x, /img/share-position/share-position-en@3x.png" />`,
		});
		this.#promo.addEventListener("click", event => {
			event.preventDefault();
			openOrInstallApplication();
		}, false);

		// precteme napoprve ze store
		const state = store.getState();
		const subscriptions = selectSubscriptions(state);
		const activeSubsciption = selectActiveSubscription(state);

		this.#build(subscriptions, activeSubsciption);

		// nahodime sledovani stavu ve store
		const wtSharePositionSubscriptions = watch(store.getState, "sharePosition.subscriptions", watchSubscriptions);
		const wtSharePositionActiveSubscription = watch<string>(store.getState, "sharePosition.activeSubscription");

		this.#storeUnsubscribeSharePosition = store.subscribe(wtSharePositionSubscriptions(newSubscriptions => {
			const activeSub = selectActiveSubscription(store.getState());

			this.#build(newSubscriptions, activeSub);
		}));
		this.#storeUnsubscribeSharePositionActive = store.subscribe(wtSharePositionActiveSubscription(activeSub => {
			const subs = selectSubscriptions(store.getState());

			this.#build(subs, activeSub);
		}));
	}

	destroy() {
		this.#storeUnsubscribeSharePosition();
		this.#storeUnsubscribeSharePositionActive();
	}

	get container() {
		return this.#container;
	}

	#build(subscriptions: Array<ISubscriptionWithDescription>, activeId: string) {
		if (subscriptions.length) {
			const newSubscriptions: { [key: string]: SubscriptionItem } = {};

			this.#emptyInfo.remove();
			this.#list.innerHTML = "";
			subscriptions.forEach(subscription => {
				// mame takovou subscription uz ted? tak jen updatujeme jeji data a poradi ve vypisu
				if (subscription.subscriptionId in this.#subscriptions) {
					const item = this.#subscriptions[subscription.subscriptionId];
					const li = node("li");

					item.data = subscription;
					item.active = activeId === subscription.subscriptionId;
					li.appendChild(item.container);
					this.#list.appendChild(li);

					// dame si item do noveho objektu a ze stareho odstranime
					newSubscriptions[subscription.subscriptionId] = item;
					delete this.#subscriptions[subscription.subscriptionId];
				// je to nova vec, vyrobime ji
				} else {
					const item = new SubscriptionItem({
						data: subscription,
						lang: CONFIG.defaultLang,
						onClick: () => {
							store.dispatch(toggleActiveSubscription(subscription.subscriptionId));
						},
						onDelete: async () => {
							Mapy.getComponent("logger").log("sharelocation_close", {
								state: item.data.sharingStatus,
							});
							try {
								await deleteSubscription(subscription.subscriptionId);
								const subs = await subscriptionsMe();

								store.dispatch(setSubscriptions(subs?.subscriptions || []));
							} catch (ex) {
								//
							}
						},
						active: activeId === subscription.subscriptionId,
						_,
					});
					const li = node("li");

					li.appendChild(item.container);
					this.#list.appendChild(li);
					newSubscriptions[subscription.subscriptionId] = item;
				}
			});
			this.#container.appendChild(this.#list);

			// co zustalo v this.#subscriptions, to uz ve skutecnosti nechceme
			for (const item in this.#subscriptions) {
				if (Object.prototype.hasOwnProperty.call(this.#subscriptions, item)) {
					this.#subscriptions[item].destroy();
				}
			}
			// nove aktualni si zapamatujeme
			this.#subscriptions = newSubscriptions;
		} else {
			// zrusime vsechno, co jsme mozna dosud ukazovali
			for (const item in this.#subscriptions) {
				if (Object.prototype.hasOwnProperty.call(this.#subscriptions, item)) {
					this.#subscriptions[item].destroy();
				}
			}
			this.#subscriptions = {};

			// zobrazime empty hlasku
			this.#list.remove();
			this.#container.appendChild(this.#emptyInfo);
		}

		this.#promo && this.#container.appendChild(this.#promo);
	}
}
